import { twMerge } from 'tailwind-merge';
import { Link } from 'react-router-dom';
import { Button, ImageWithPlaceholder } from '@wbk/ui';
import { ContentfulMedia } from '@wbk/contentful';
import type { ContentfulMedia as ContentfulMediaType } from '@wbk/contentful/api';

interface GuideCardProps {
  title: string;
  description?: string;
  image?: ContentfulMediaType;

  buttonText?: string;
  onButtonClick?: () => void;
  className?: string;
  metaData?: string[];
  showBookIcon?: boolean;
  location?: string;
  contentClassName?: string;
  href?: string;
  homepage?: boolean;
}

const GuideCard = ({
  title,
  description,
  image,
  homepage,
  buttonText = 'Download',
  onButtonClick,
  className,
  metaData,
  showBookIcon = true,
  location,
  contentClassName,
  href,
}: GuideCardProps) => {
  return (
    <div
      className={twMerge(
        'group relative mx-auto h-[16rem] overflow-hidden rounded-2xl border border-white/10 transition-all duration-200 hover:border-white/20 sm:w-full md:h-[26rem] md:rounded-3xl dark:bg-black dark:text-[#F4F4F5]',
        className
      )}
    >
      <div className='= relative h-full'>
        {image && (
          <ContentfulMedia
            media={image}
            className='h-full w-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-110'
          />
        )}

        <div className='absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/40 transition-all duration-300 ease-in-out group-hover:opacity-0' />

        {showBookIcon && (
          <div className='absolute end-2 top-2 sm:end-4 sm:top-4'>
            <div className='rounded-full bg-[#FFFFFF33] p-2 backdrop-blur-sm'>
              <ImageWithPlaceholder
                src='/icons/book-open.svg'
                alt='Book Icon'
                className='size-4 object-cover sm:size-6'
                draggable={false}
              />
            </div>
          </div>
        )}

        <div className='absolute inset-x-0 bottom-0 flex flex-col'>
          {location && (
            <p className='flex items-center gap-1 text-balance px-2 text-xs font-semibold text-[#F4F4F5] sm:px-4 sm:text-[13px]'>
              <svg
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M8.33325 2C11.3333 2 13.3333 4.5 13.3333 7C13.3333 8.81564 10.6961 11.6865 9.2522 13.1221C8.73143 13.6397 7.91347 13.6613 7.37231 13.165C5.91997 11.8331 3.33325 9.15783 3.33325 7C3.33325 4 5.33325 2 8.33325 2ZM8.33325 4.66699C7.41289 4.66699 6.66644 5.41268 6.66626 6.33301C6.66626 7.25348 7.41278 8 8.33325 8C9.25373 8 10.0002 7.25348 10.0002 6.33301C10.0001 5.41268 9.25362 4.66699 8.33325 4.66699Z'
                  fill='#D4D4D8'
                />
              </svg>

              {location}
            </p>
          )}

          <div
            className={twMerge(
              'flex flex-col gap-2 px-2 pb-2 pt-1 backdrop-blur-[1px] sm:px-4 sm:pb-4',
              contentClassName
            )}
          >
            <div className='flex flex-col gap-2'>
              <div className='flex flex-col gap-1'>
                <h2 className='text-balance text-[15px] font-semibold leading-5 sm:text-[17px]'>
                  {title}
                </h2>
                {!homepage && <p className='text-xs sm:text-sm'>{description}</p>}
              </div>
              <p className='text-xs capitalize text-[#A1A1AA] md:text-sm'>
                {metaData?.join(' • ')}
              </p>
            </div>
            <div className='flex justify-between md:flex-row md:items-center'>
              {homepage && <p className='text-xs sm:text-sm'>{description}</p>}
              <Link to={href || '#'} target='_blank' rel='noreferrer noopener'>
                <Button
                  onClick={onButtonClick}
                  className='bg-primary-100 hover:bg-primary-100/90 sm:text-body-S text-body-XXS mb-3 flex w-full items-center gap-1 rounded-full px-4 py-1 font-semibold capitalize transition-colors sm:py-2'
                >
                  {buttonText} <img src='/icons/DownloadIcon.svg' alt='' />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuideCard;
